import z from 'zod';
import { objectIdSchema, quantitySchema } from '../../validation';
import { vehicleTypesSchema } from '../../common';
import { lastSeenOnlineSchema, userMetaPresenceSchema } from '../user/user.meta.presence.model';
import { referralAttributionSchema } from '../referral/referral.attribution.model';

/**
 * `Org.meta` — ops / analytics bag (signup estimates + heartbeats).
 * Same role as `User.meta`: not public identity. A DTO strip is not
 * wired yet; do not leak this onto a future public org projection.
 *
 * Signup estimates are optional so a first presence heartbeat can
 * `$set` `meta.lastSeenOnline` on an org that never filled the
 * signup form without failing Org parse.
 */
export const orgMetaSchema = z.object({
  /** Estimated fleet size from signup. Zero / absent = not applicable. */
  estimatedVehicleTotal: quantitySchema.optional(),
  /** Estimated monthly order volume from signup. Zero / absent = not applicable. */
  estimatedOrdersPerMonth: quantitySchema.optional(),
  /** Estimated vehicle types from signup. Empty / absent = not yet estimated. */
  vehicleTypes: z.array(vehicleTypesSchema).optional(),

  /**
   * Most-recent "a non-driver member of this org was online"
   * heartbeat. Same shape as `User.meta.lastSeenOnline`. Bumped by
   * `UserMetaService.recordPresenceHeartbeat` when the active
   * member's `memberType !== 'driver'`, for
   * `User.currentSignedInOrgId` only.
   */
  lastSeenOnline: lastSeenOnlineSchema.optional(),

  /**
   * Per-(app, platform) first/last-seen for **every** member of this
   * org, including drivers — keyed on the member's
   * `currentSignedInOrgId`. Same shape as `User.meta.presence`.
   *
   * Distinct from {@link lastSeenOnline}, which stays the non-driver
   * "org is awake" snapshot used for outreach.
   */
  presence: userMetaPresenceSchema.optional(),

  /**
   * Which referral link brought this org in, copied from the Lead's
   * `meta.sourceMeta.referral` at signup (or stamped directly when the
   * signup payload carried a `referralCode` and no Lead existed). Read by
   * `ReferralRewardService` when a Billing this org pays settles, so the
   * partner is found without a Lead join. Written once.
   */
  referral: referralAttributionSchema.optional(),

  /**
   * Tombstone: this org was merged into another (`AccountOpsService.mergeOrgs`).
   * Its data now lives on the survivor; the doc stays, blocked, for audit.
   */
  mergedIntoOrgId: objectIdSchema.optional(),
  /** Why the org was retired without a merge. */
  retiredReason: z.enum(['account_type_changed', 'owner_deleted']).optional(),
  /** Set with `retiredReason: 'account_type_changed'` — the owner who became a freelancer driver. */
  convertedToUserId: objectIdSchema.optional(),
});
export type OrgMeta = z.infer<typeof orgMetaSchema>;
